/**
 * Simple Creator Service - Clean integration with creator dashboard
 * Fetches creator profiles from the same Firestore database
 */

import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc,
  orderBy,
} from 'firebase/firestore';
import { db, auth } from '../firebase';

/**
 * Get all visible creators from the creator dashboard
 * @param {Function} onUpdate - Callback function for updates
 * @param {Function} onError - Error callback function
 */
export const fetchVisibleCreators = async (onUpdate, onError) => {
  try {
    // Check if user is authenticated
    if (!auth.currentUser) {
      onError(new Error('No authenticated user'));
      return;
    }
    
    // Note: Security is handled by Firestore rules and application-level filtering
    
    // Query only creator profiles to satisfy Firestore rules
    const usersRef = collection(db, 'users');
    const creatorsQuery = query(usersRef, where('userType', '==', 'creator'));
    const snapshot = await getDocs(creatorsQuery);
    
    const creators = [];
    
    for (const docSnapshot of snapshot.docs) {
      try {
        const creatorData = docSnapshot.data();
      
      // Skip non-creators
      if (creatorData.userType !== 'creator') {
        continue;
      }
      
      // Skip creators that are not available to brands
      if (creatorData.availability !== 'available' && creatorData.isProfileVisible !== true) {
        continue;
      }
      
      // Skip creators with no name at all (but allow other missing fields)
      if (!creatorData.name && !creatorData.displayName) {
        continue;
      }
      
      // Map creator data to brand dashboard format with generous fallbacks
      const mappedCreator = {
        id: docSnapshot.id,
        name: creatorData.name || creatorData.displayName || 'Creator',
        handle: creatorData.handle ? `@${creatorData.handle}` : `@creator${docSnapshot.id.slice(0, 6)}`,
        age: creatorData.age || creatorData.ageRange || 'Not specified',
        gender: creatorData.gender || 'Not specified',
        location: creatorData.location || creatorData.city || 'Location not specified',
        generation: creatorData.generation || creatorData.ageGroup || 'Not specified',
        
        // Map niches - creator dashboard stores as array
        niche: creatorData.niches && creatorData.niches.length > 0 ? creatorData.niches.join('.') : 'General Content',
        niches: creatorData.niches && creatorData.niches.length > 0 ? creatorData.niches : ['General Content'],
        
        // Map specialties
        specialties: creatorData.specialties || creatorData.contentTypes || ['Content Creation'],
        contentSpecialties: creatorData.specialties || creatorData.contentTypes || ['Content Creation'],
        
        // Map creator level
        creatorLevel: creatorData.creatorLevel || creatorData.level || 'rising_star',
        tier: (creatorData.creatorLevel === 'Pro' || creatorData.creatorLevel === 'pro_creator' || creatorData.level === 'pro' || creatorData.creatorStatus === 'Pro') ? 'Pro' : 'Rising Star',
        
        // Map profile image - try multiple possible fields
        profileImage: creatorData.profileImage || creatorData.profileImagePath || creatorData.avatar || creatorData.photoURL || '/images/profile-placeholder.svg',
        image: creatorData.profileImage || creatorData.profileImagePath || creatorData.avatar || creatorData.photoURL || '/images/profile-placeholder.svg',
        
        // Map card image positioning (from creator dashboard)
        cardImagePosition: creatorData.cardImagePosition || { x: 0, y: 0 },
        cardImageScale: creatorData.cardImageScale || 1,
        profileImageAlignment: creatorData.profileImageAlignment || 'center',
        
        // Debug: Log positioning data
        debug: {
          cardImagePosition: creatorData.cardImagePosition,
          cardImageScale: creatorData.cardImageScale,
          profileImageAlignment: creatorData.profileImageAlignment
        },
        
        // Map bio (short description for cards and portfolio top)
        bio: creatorData.bio || 'Content creator passionate about creating engaging content.',
        
        // Map about (detailed description for portfolio about section)
        about: creatorData.about || creatorData.description || '',
        
        // Map social stats with fallbacks
        followers: creatorData.followers || creatorData.followerCount || 0,
        engagement: creatorData.engagement || creatorData.engagementRate || 0,
        views: creatorData.views || creatorData.viewCount || 0,
        
        // Map metrics for CreatorModal
        metrics: [
          `Avg views: ${(creatorData.views || creatorData.viewCount || 0) ? Math.round((creatorData.views || creatorData.viewCount || 0) / 1000) + 'k' : '0k'}`,
          `Engagement: ${creatorData.engagement || creatorData.engagementRate || 0}%`,
          `Followers: ${(creatorData.followers || creatorData.followerCount || 0) ? Math.round((creatorData.followers || creatorData.followerCount || 0) / 1000) + 'k' : '0k'}`
        ],
        
        // Portfolio videos load when a creator profile is opened (not for the grid list).
        examples: [],
        
        // Map brands worked with
        brands: creatorData.brandsWorkedWith || creatorData.collaborations || creatorData.partners || [],
        
        // Map content they won't create
        notFor: creatorData.notFitFor || creatorData.wontCreate || creatorData.exclusions || [],
        
        // Map hobbies
        hobbies: creatorData.hobbies ? (Array.isArray(creatorData.hobbies) ? creatorData.hobbies.join(', ') : creatorData.hobbies) : 'Various interests',
        
        // Additional fields
        isNew: false,
        createdAt: creatorData.createdAt || creatorData.joinedAt || new Date(),
        isProfileVisible: creatorData.isProfileVisible !== false,
        userType: creatorData.userType || 'creator'
      };
      
      creators.push(mappedCreator);
      } catch (creatorError) {
        console.error(`Error processing creator ${docSnapshot.id}:`, creatorError);
        // Continue with other creators instead of failing completely
      }
    }
    
    onUpdate(creators);
    
  } catch (error) {
    console.error('Error fetching creators:', error);
    onError(error);
  }
};

const dedupePortfolioVideos = (videos = []) => {
  const seen = new Set();
  return videos.filter((video) => {
    const key = video.url || '';
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Fetch all portfolio videos for a creator (niche subcollections + legacy array).
 * @param {string} userId - Creator's user ID
 * @param {Array} niches - Array of niche names
 * @returns {Promise<Array>} Array of content video examples
 */
const fetchCreatorContentVideos = async (userId, niches) => {
  try {
    const normalizedNiches = Array.isArray(niches) && niches.length > 0 ? niches : ['General Content'];
    const allVideos = [];

    const nicheVideoGroups = await Promise.all(
      normalizedNiches.map(async (niche) => {
        try {
          const cleanNiche = niche.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
          const nicheRef = collection(db, `users/${userId}/portfolio_${cleanNiche}`);
          const nicheQuery = query(nicheRef, orderBy('createdAt', 'desc'));
          const querySnapshot = await getDocs(nicheQuery);

          const nicheVideos = [];
          querySnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            if (data?.url) {
              nicheVideos.push({
                id: docSnap.id,
                title: data.title || data.label,
                url: data.url,
                niche,
              });
            }
          });
          return nicheVideos;
        } catch (nicheError) {
          console.log(`Could not fetch portfolio videos for niche ${niche}:`, nicheError);
          return [];
        }
      })
    );

    nicheVideoGroups.forEach((videos) => {
      allVideos.push(...videos);
    });

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const legacyVideos = userData.portfolioVideos || [];

      legacyVideos.forEach((video, index) => {
        if (!video?.url) return;

        const videoNiche = video.niche || normalizedNiches[0] || 'General';
        const matchesNiche = normalizedNiches.some(
          (n) => n.toLowerCase() === String(videoNiche).toLowerCase()
        );

        if (matchesNiche) {
          allVideos.push({
            id: `legacy_${index}`,
            title: video.title,
            url: video.url,
            niche: videoNiche,
          });
        }
      });
    }

    return dedupePortfolioVideos(allVideos).map((video, index) => ({
      id: video.id || `video_${index}`,
      label: video.title || `${video.niche || 'Content'} Video`,
      video: video.url || '',
      niche: video.niche || 'General',
    }));
  } catch (error) {
    console.error('Error fetching content videos:', error);
    return [];
  }
};

export const fetchCreatorPortfolioExamples = fetchCreatorContentVideos;

/**
 * Get a specific creator by ID (for display in Hand-In, modals, etc.).
 * Returns profile regardless of availability – so existing assignments/submissions
 * always show correct name and image. Discoverability (Find Creators) is controlled
 * by fetchVisibleCreators, which still filters by availability.
 * @param {string} creatorId - Creator's UID
 * @returns {Promise<Object|null>}
 */
export const getCreatorById = async (creatorId) => {
  try {
    const creatorRef = doc(db, 'users', creatorId);
    const creatorDoc = await getDoc(creatorRef);
    
    if (creatorDoc.exists()) {
      const creatorData = creatorDoc.data();
      if (creatorData.userType === 'creator') {
        // Map the same way as in fetchVisibleCreators with generous fallbacks
        return {
          id: creatorDoc.id,
          name: creatorData.name || creatorData.displayName || 'Creator',
          handle: creatorData.handle ? `@${creatorData.handle}` : `@creator${creatorDoc.id.slice(0, 6)}`,
          age: creatorData.age || creatorData.ageRange || 'Not specified',
          gender: creatorData.gender || 'Not specified',
          location: creatorData.location || creatorData.city || 'Location not specified',
          generation: creatorData.generation || creatorData.ageGroup || 'Not specified',
          niche: creatorData.niches && creatorData.niches.length > 0 ? creatorData.niches.join('.') : 'General Content',
          niches: creatorData.niches && creatorData.niches.length > 0 ? creatorData.niches : ['General Content'],
          specialties: creatorData.specialties || creatorData.contentTypes || ['Content Creation'],
          creatorLevel: creatorData.creatorLevel || creatorData.level || 'rising_star',
          tier: (creatorData.creatorLevel === 'Pro' || creatorData.creatorLevel === 'pro_creator' || creatorData.level === 'pro' || creatorData.creatorStatus === 'Pro') ? 'Pro' : 'Rising Star',
          profileImage: creatorData.profileImage || creatorData.profileImagePath || creatorData.avatar || creatorData.photoURL || '/images/profile-placeholder.svg',
          image: creatorData.profileImage || creatorData.profileImagePath || creatorData.avatar || creatorData.photoURL || '/images/profile-placeholder.svg',
          
          // Map card image positioning (from creator dashboard)
          cardImagePosition: creatorData.cardImagePosition || { x: 0, y: 0 },
          cardImageScale: creatorData.cardImageScale || 1,
          profileImageAlignment: creatorData.profileImageAlignment || 'center',
          bio: creatorData.bio || 'Content creator passionate about creating engaging content.',
          about: creatorData.about || creatorData.description || '',
          metrics: [
            `Avg views: ${(creatorData.views || creatorData.viewCount || 0) ? Math.round((creatorData.views || creatorData.viewCount || 0) / 1000) + 'k' : '0k'}`,
            `Engagement: ${creatorData.engagement || creatorData.engagementRate || 0}%`,
            `Followers: ${(creatorData.followers || creatorData.followerCount || 0) ? Math.round((creatorData.followers || creatorData.followerCount || 0) / 1000) + 'k' : '0k'}`
          ],
          examples: await fetchCreatorContentVideos(creatorDoc.id, creatorData.niches || []),
          brands: creatorData.brandsWorkedWith || creatorData.collaborations || creatorData.partners || [],
          notFor: creatorData.notFitFor || creatorData.wontCreate || creatorData.exclusions || [],
          hobbies: creatorData.hobbies ? (Array.isArray(creatorData.hobbies) ? creatorData.hobbies.join(', ') : creatorData.hobbies) : 'Various interests',
          isProfileVisible: creatorData.isProfileVisible !== false,
          userType: creatorData.userType || 'creator'
        };
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching creator by ID:', error);
    return null;
  }
};
